import React, { useState, KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useDialog } from '../../context/DialogContext';
import { useSources } from '../../context/SourcesContext';
import { validateAddSource } from '../../lib/addSourceForm';
import { Button, Modal, Spinner } from '../../uikit';

// COM-640: rebuilt on `uikit/Modal` with kit `Button`s, replacing the raw-MUI
// version that shipped two visible bugs (Figma 4036:85796 / design doc
// 2026-08-18-com640-refresh-scanning-design.md):
//   1. MUI's floating `TextField` label assumes the DEFAULT input padding;
//      theme.ts's `MuiOutlinedInput` override (12px input padding, 8px
//      radius) changes that geometry without updating the label's own
//      transform-origin math, so the un-shrunk label sat visibly low inside
//      the field ("text sitting low"). Placeholders sidestep the whole
//      problem - there is no floating label to mis-position.
//   2. The actions row used MUI's own `DialogActions` (8px padding), which
//      reads as flush-against-the-paper-edge next to the kit's larger pill
//      buttons. `Modal`'s own `actions` slot already applies real padding
//      (px: 3, pb: 3, gap: 1.5) - no local override needed here.
const AddSourceDialog: React.FC = () => {
  const { isAddDialogOpen, closeAddDialog } = useDialog();
  const { addSource } = useSources();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  // COM-640: the backend's REFUSAL of this specific submission (a duplicate
  // URL, or COM-639's 100-source cap - `addSource`'s Promise rejects for
  // both, see SourcesContext.tsx). Rendered inline, in this still-open
  // dialog - the behaviour change this task makes. Before this task the
  // dialog closed immediately on submit with no listener for the outcome at
  // all, so a refusal surfaced only as a generic toast after the dialog (and
  // the user's attention) had already moved on. Distinct from `urlError`
  // below: that's client-side format validation that never reaches the
  // server; this is the server's answer to an otherwise well-formed one.
  const [refusalError, setRefusalError] = useState<string | null>(null);
  // COM-640: true from submit until `addSource`'s Promise settles - which
  // resolves the moment `sourceAdded` confirms the row exists, NOT once the
  // scan it kicks off finishes. That distinction is the whole point: the
  // opposite ("in flight" spans the scan) is the natural first assumption,
  // and it's wrong here - there is nothing left for THIS dialog to report
  // once the row exists, so it closes and `ScanIndicator` (driven by
  // SourcesContext's `scanningSourceCount`/`sourceScanCompletedCount`)
  // carries the rest of the story for however long the scan actually takes.
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setName('');
    setUrl('');
    setUrlError(null);
    setRefusalError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    // Ignore close attempts (the X button, backdrop click, Escape) while a
    // submission is in flight - `Modal`'s `onClose` is this dialog's ONLY
    // close path (no separate escape/backdrop wiring), so gating it here is
    // enough. The reason to gate it at all: the refusal above only has
    // somewhere to render because the dialog stays open through the round
    // trip - letting the user close out from under a pending submission
    // would defeat that for a refusal that arrives a moment later.
    if (submitting) {
      return;
    }
    resetForm();
    closeAddDialog();
  };

  const handleSubmit = () => {
    const { urlError: nextUrlError } = validateAddSource({ name, url });
    if (nextUrlError) {
      setUrlError(nextUrlError);
      return;
    }

    setUrlError(null);
    setRefusalError(null);
    setSubmitting(true);

    // Name is optional (Jira AC + Confluence) - `.trim()` alone, no
    // required-field check, mirrors `validateAddSource`'s own decision not
    // to validate it at all.
    addSource(name.trim(), url.trim()).then(
      () => {
        resetForm();
        closeAddDialog();
      },
      (error: Error) => {
        setSubmitting(false);
        setRefusalError(error.message || 'Failed to add the source. Please try again.');
      }
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      open={isAddDialogOpen}
      title="Add New Source"
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <Spinner sx={{ width: 16, height: 16 }} /> : undefined}
          >
            {submitting ? 'Adding…' : 'Add'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder="Source name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={submitting}
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (urlError) setUrlError(null);
          }}
          onKeyDown={handleKeyDown}
          error={!!urlError}
          helperText={urlError}
          disabled={submitting}
        />
        {refusalError && (
          <Typography fontSize={14} fontWeight={500} color="error.main">
            {refusalError}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default AddSourceDialog;
